<template>
  <div class="teacher-form-container">
    <div class="page-header">
      <el-button link class="back-btn" @click="handleCancel">
        <el-icon><ArrowLeft /></el-icon>
        {{ $t('common.back') }}
      </el-button>
      <span class="page-title">{{ title }}</span>
      <el-tag :type="teacherForm.status === 1 ? 'success' : 'info'" size="large">
        {{ statusText }}
      </el-tag>
    </div>
    
    <el-card class="form-card">
      <div class="card-header">
        <span class="header-title">{{ $t('teacher.basicInfo') }}</span>
      </div>
      
      <el-form 
        :model="teacherForm" 
        :rules="rules" 
        ref="formRef" 
        :label-width="isMobile ? 'auto' : '140px'"
        :label-position="isMobile ? 'top' : 'left'"
        class="teacher-form"
      >
        <el-form-item :label="$t('teacher.name')" prop="name">
          <el-input 
            v-model="teacherForm.name" 
            :placeholder="$t('teacher.enterName')"
            clearable
          />
        </el-form-item>

        <el-form-item :label="$t('teacher.phone')" prop="phone">
          <el-input 
            v-model="teacherForm.phone" 
            :placeholder="$t('teacher.enterPhone')"
            clearable
          />
        </el-form-item>

        <el-form-item :label="$t('teacher.ruleKind')" prop="ruleKind">
          <el-select 
            v-model="teacherForm.ruleKind" 
            :placeholder="$t('teacher.selectRuleKind')"
            style="width: 100%"
          >
            <el-option :label="$t('teacher.none')" :value="0" />
            <el-option :label="$t('teacher.customer')" :value="1" />
            <el-option :label="$t('teacher.teacher')" :value="2" />
            <el-option :label="$t('teacher.admin')" :value="3" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('teacher.status')" prop="status">
          <el-select 
            v-model="teacherForm.status" 
            :placeholder="$t('teacher.selectStatus')"
            style="width: 100%"
          >
            <el-option :label="$t('teacher.inactive')" :value="0" />
            <el-option :label="$t('teacher.active')" :value="1" />
            <el-option :label="$t('teacher.deleted')" :value="2" />
            <el-option :label="$t('teacher.blocked')" :value="3" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('teacher.age')" prop="age">
          <el-input-number 
            v-model="teacherForm.age" 
            :min="5"
            :max="100"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item :label="$t('teacher.workExperience')" prop="workExperienceYear">
          <el-input-number 
            v-model="teacherForm.workExperienceYear" 
            :min="0"
            :precision="1"
            controls-position="right"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item :label="$t('teacher.remark')" prop="remark">
          <el-input 
            v-model="teacherForm.remark" 
            :placeholder="$t('teacher.enterRemark')"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            clearable
          />
        </el-form-item>

        <el-form-item :label="$t('teacher.isVisibleForMiniApp')" prop="isVisibleForMiniApp">
          <el-switch
            v-model="teacherForm.isVisibleForMiniApp"
            :active-text="$t('teacher.showInMiniApp')"
            :inactive-text="$t('teacher.hideFromMiniApp')"
            active-color="#13ce66"
            inactive-color="#ff4949"
          />
        </el-form-item>

        <el-form-item :label="$t('teacher.subscribeAllBookingNotifications')" prop="subscribeAllBookingNotifications">
          <el-switch
            v-model="teacherForm.subscribeAllBookingNotifications"
            :active-text="$t('teacher.enabled')"
            :inactive-text="$t('teacher.disabled')"
            active-color="#13ce66"
            inactive-color="#ff4949"
          />
        </el-form-item>

        <el-form-item>
          <el-button 
            type="primary" 
            @click="submitForm"
            :loading="loading"
            class="submit-btn"
          >
            {{ submitText }}
          </el-button>
          <el-button @click="handleCancel">
            {{ $t('common.cancel') }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { ArrowLeft } from '@element-plus/icons-vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Teacher Form'
  },
  submitText: {
    type: String,
    default: 'Submit'
  },
  initialData: {
    type: Object,
    default: () => ({})
  },
  onSubmit: {
    type: Function,
    required: true
  },
  loadData: {
    type: Function,
    default: null
  }
})

const router = useRouter()
const { t } = useI18n()
const formRef = ref()
const loading = ref(false)

// Mobile detection
const isMobile = ref(window.innerWidth <= 768)
const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
}
onMounted(() => {
  window.addEventListener('resize', handleResize)
})
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const defaultForm = {
  id: 0,
  name: '',
  phone: '',
  status: 1,
  remark: '',
  level: 0,
  avatar: '',
  age: 0,
  ruleKind: 0,
  workExperienceYear: 0,
  isVisibleForMiniApp: false,
  subscribeAllBookingNotifications: false
}

const teacherForm = reactive({ ...defaultForm })

const statusText = computed(() => {
  const statusMap = {
    0: t('teacher.inactive'),
    1: t('teacher.active'),
    2: t('teacher.deleted'),
    3: t('teacher.blocked')
  }
  return statusMap[teacherForm.status] || '-'
})

const rules = {
  name: [{ required: true, message: 'Please enter name', trigger: 'blur' }],
  phone: [{ required: true, message: 'Please enter phone', trigger: 'blur' }],
  ruleKind: [{ required: true, message: 'Please select rule kind', trigger: 'change' }],
  status: [{ required: true, message: 'Please select status', trigger: 'change' }],
  age: [{ required: true, message: 'Please enter age', trigger: 'blur' }]
}

// Initialize form data
const initForm = () => {
  Object.assign(teacherForm, {
    ...defaultForm,
    ...props.initialData
  })
}

const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        await props.onSubmit(teacherForm)
      } catch (error) {
        ElMessage.error(error.message || 'Operation failed')
      } finally {
        loading.value = false
      }
    }
  })
}

const handleCancel = () => {
  router.push('/teacher')
}

// Load data if loadData function is provided
onMounted(async () => {
  if (props.loadData) {
    loading.value = true
    try {
      const data = await props.loadData()
      Object.assign(teacherForm, data)
    } catch (error) {
      ElMessage.error('Failed to load data')
    } finally {
      loading.value = false
    }
  } else {
    initForm()
  }
})
</script>

<style lang="scss" scoped>
.teacher-form-container {
  padding: 24px;
  background: #f0f2f5;
  min-height: calc(100vh - 140px);
  
  @media (max-width: 768px) {
    padding: 12px;
    min-height: calc(100vh - 100px);
  }
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding: 0 4px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }
  
  .back-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #606266;
    padding: 8px 12px;
    border-radius: 4px;
    transition: all 0.2s;
    margin-right: 8px;

    @media (max-width: 768px) {
      padding: 6px 8px;
      font-size: 13px;
      margin-right: 0;
      
      .el-icon {
        font-size: 14px;
      }
    }

    &:hover {
      color: #409eff;
      background: #ecf5ff;
    }

    .el-icon {
      font-size: 16px;
    }
  }

  .page-title {
    flex: 1;
    font-size: 18px;
    font-weight: 600;
    
    @media (max-width: 768px) {
      font-size: 16px;
      flex: none;
      order: 3;
      width: 100%;
    }
  }
  
  :deep(.el-tag) {
    padding: 6px 16px;
    font-size: 14px;
    border-radius: 20px;
    
    @media (max-width: 768px) {
      padding: 4px 12px;
      font-size: 12px;
    }
  }
}

.form-card {
  background: #fff;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    border-radius: 12px;
    
    :deep(.el-card__body) {
      padding: 16px 12px;
    }
  }
  
  .card-header {
    margin-bottom: 24px;
    
    @media (max-width: 768px) {
      margin-bottom: 16px;
    }
    
    .header-title {
      font-size: 15px;
      font-weight: 600;
      color: #333;
      
      @media (max-width: 768px) {
        font-size: 14px;
      }
    }
  }
}

.teacher-form {
  max-width: 600px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
  
  :deep(.el-form-item__label) {
    font-weight: 500;
    color: #606266;
    
    @media (max-width: 768px) {
      padding-bottom: 4px;
      height: auto;
      line-height: 1.4;
    }
  }
  
  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    border-radius: 4px;
    
    @media (max-width: 768px) {
      font-size: 16px; /* Prevent iOS zoom on input */
    }
  }
  
  :deep(.el-input-number) {
    width: 100%;
    
    @media (max-width: 768px) {
      :deep(.el-input-number__decrease),
      :deep(.el-input-number__increase) {
        width: 28px;
      }
      
      :deep(.el-input__wrapper) {
        padding-right: 58px;
      }
    }
  }
  
  :deep(.el-select) {
    width: 100%;
    
    @media (max-width: 768px) {
      :deep(.el-input__inner) {
        font-size: 16px;
      }
    }
  }
  
  :deep(.el-form-item) {
    @media (max-width: 768px) {
      margin-bottom: 18px;
    }
  }
  
  :deep(.el-switch__label) {
    font-size: 14px;
    
    @media (max-width: 768px) {
      font-size: 13px;
    }
  }
  
  :deep(.el-form-item__error) {
    @media (max-width: 768px) {
      font-size: 12px;
    }
  }
  
  .submit-btn {
    width: 140px;
    height: 40px;
    font-size: 15px;
    font-weight: 500;
    
    @media (max-width: 768px) {
      width: 100%;
      height: 44px;
      font-size: 16px;
    }
  }
  
  :deep(.el-form-item:last-child) {
    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 12px;
      
      .el-button {
        width: 100%;
        height: 44px;
        font-size: 16px;
      }
    }
  }
}
</style>
